import React, { useEffect, useState } from "react";
import {
  PaymentForm,
  CreditCard,
  ApplePay,
  GooglePay,
} from "react-square-web-payments-sdk";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Check, CreditCard as CardIcon, Banknote, Smartphone, Clock, ShieldCheck } from "lucide-react";

const BRAND_LABEL = {
  VISA: "Visa", MASTERCARD: "Mastercard", AMERICAN_EXPRESS: "Amex",
  DISCOVER: "Discover", JCB: "JCB", UNIONPAY: "UnionPay",
};

/**
 * Payment selector shown after booking confirmation.
 * Supports: Square Card (incl. Apple Pay / Google Pay), saved cards (card-on-file), Zelle, Venmo, Cash, Pay-later.
 */
/** amountOverride (dispatcher only): charge exactly this dollar amount instead
 *  of the fare — powers "Charge New Card" with a manual amount / remaining balance. */
export default function PaymentSelector({ booking, onPaid, dispatcherMode = false, amountOverride = null, chargeReason = null }) {
  const { user } = useAuth();
  const [methods, setMethods] = useState(null);
  const [savedCards, setSavedCards] = useState([]);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  // Customer-added tip (in dollars). Hidden in dispatcherMode since dispatch
  // already controls gratuity through the Fare Breakdown panel. Quick picks
  // 10/15/20% are wired against the booking's `fare` (already gratuity-
  // bundled), so this is *additional* tip on top.
  const [tip, setTip] = useState(0);

  useEffect(() => {
    api.get("/payments/methods").then(r => setMethods(r.data)).catch(() => setMethods({}));
    // Saved cards: look up by the BOOKING's customer email when running in
    // dispatcher mode (so the dispatcher sees the customer's saved cards, not
    // their own). Falls back to the logged-in user's cards otherwise.
    if (dispatcherMode && booking?.customer_email) {
      api.get(`/payments/cards/by-email?email=${encodeURIComponent(booking.customer_email)}`)
        .then(r => setSavedCards(r.data || [])).catch(() => setSavedCards([]));
    } else if (user) {
      api.get("/payments/cards/me").then(r => setSavedCards(r.data || [])).catch(() => setSavedCards([]));
    }
  }, [user, dispatcherMode, booking?.customer_email]);

  if (!methods) {
    return <div className="text-sm text-[#A1A1AA]">Loading payment options…</div>;
  }

  // Round trip: booking creation attaches `return_leg` (the auto-created
  // reverse ride). Checkout must show + charge BOTH legs together — clients
  // previously only paid the one-way price. Backend endpoints accept
  // `include_linked_return` and mark both bookings paid under one payment.
  const override = dispatcherMode && amountOverride != null && Number(amountOverride) > 0 ? Number(amountOverride) : null;
  const returnLeg = !override && booking.return_leg && booking.return_leg.fare != null ? booking.return_leg : null;
  const outboundFare = Number(booking.fare) || 0;
  const returnFare = returnLeg ? Number(returnLeg.fare) || 0 : 0;
  const baseAmount = override ?? (outboundFare + returnFare);
  const tipAmount = Math.max(0, Number(tip) || 0);
  const totalWithTip = baseAmount + tipAmount;
  const amount = totalWithTip.toFixed(2);
  const canOfferTip = !dispatcherMode;

  const handleSquareToken = async (token) => {
    setBusy(true);
    try {
      // One-shot charge — the backend vaults the card as a Payment Method on
      // the client's profile from the completed payment automatically.
      const r = await api.post("/payments/square/charge", {
        booking_id: booking.booking_id,
        source_id: token.token,
        tip_amount: tipAmount,
        include_linked_return: !!returnLeg,
        ...(override ? { amount: override, reason: chargeReason || "Dispatcher charge" } : {}),
      });
      const saved = r.data?.saved_card && !r.data.saved_card.already_on_file;
      toast.success(`${returnLeg ? "Both trips paid" : "Payment complete"}${tipAmount > 0 ? ` (incl. $${tipAmount.toFixed(2)} tip)` : ""}${saved ? " — card saved to your profile for next trip." : " — thank you!"}`);
      onPaid({ ...booking, payment_status: "paid", payment_method: "card", payment_id: r.data.payment_id });
      if (saved && user) api.get("/payments/cards/me").then(rr => setSavedCards(rr.data || [])).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed");
    } finally {
      setBusy(false);
    }
  };

  const chargeSaved = async (saved_card_id) => {
    setBusy(true);
    try {
      const r = await api.post("/payments/charge-saved", {
        booking_id: booking.booking_id,
        saved_card_id,
        tip_amount: tipAmount,
        include_linked_return: !!returnLeg,
        ...(override ? { amount: override, reason: chargeReason || "Dispatcher charge" } : {}),
      });
      toast.success(`Charged saved card${returnLeg ? " for both trips" : ""}${tipAmount > 0 ? ` (incl. $${tipAmount.toFixed(2)} tip)` : ""} — thank you!`);
      onPaid({ ...booking, payment_status: "paid", payment_method: "card_on_file", payment_id: r.data.payment_id });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed");
    } finally {
      setBusy(false);
    }
  };

  const handleManual = async (method, reference = null) => {
    setBusy(true);
    try {
      const r = await api.post("/payments/manual", {
        booking_id: booking.booking_id,
        customer_email: booking.customer_email,
        method,
        reference,
        include_linked_return: !!returnLeg,
      });
      const status = r.data.status;
      if (status === "pending") {
        toast.success(`Recorded — dispatch will confirm receipt of your ${method.toUpperCase()} payment.`);
        onPaid({ ...booking, payment_status: "pending", payment_method: method });
      } else {
        toast.success("You're all set — pay your chauffeur on arrival.");
        onPaid({ ...booking, payment_status: "pay_later", payment_method: method });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record");
    } finally {
      setBusy(false);
    }
  };

  const fmtLeg = (s) => s ? new Date(s).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";

  return (
    <div data-testid="payment-selector" className="space-y-4">
      {/* Round trip breakdown — make it crystal clear the payment covers BOTH rides */}
      {returnLeg && (
        <div data-testid="round-trip-payment-summary" className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4 space-y-3">
          <div className="overline text-[#D4AF37]">Round trip · one payment covers both rides</div>
          <div className="flex items-start justify-between gap-3 text-sm">
            <div className="min-w-0">
              <div className="text-[#F5F2EB]">Outbound · {fmtLeg(booking.pickup_datetime)}</div>
              <div className="text-[11px] text-[#71717A] mt-0.5 truncate">{booking.pickup_address} → {booking.dropoff_address}</div>
            </div>
            <div className="text-[#F5F2EB] whitespace-nowrap" data-testid="rt-outbound-fare">${outboundFare.toFixed(2)}</div>
          </div>
          <div className="flex items-start justify-between gap-3 text-sm">
            <div className="min-w-0">
              <div className="text-[#F5F2EB]">Return · {fmtLeg(returnLeg.pickup_datetime)}</div>
              <div className="text-[11px] text-[#71717A] mt-0.5 truncate">{returnLeg.pickup_address} → {returnLeg.dropoff_address}</div>
            </div>
            <div className="text-[#F5F2EB] whitespace-nowrap" data-testid="rt-return-fare">${returnFare.toFixed(2)}</div>
          </div>
          <div className="border-t border-[#D4AF37]/25 pt-2.5 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-[0.18em] text-[#A1A1AA]">Round trip total</span>
            <span className="font-serif text-xl text-[#D4AF37]" data-testid="rt-combined-total">${baseAmount.toFixed(2)}</span>
          </div>
        </div>
      )}

      <div className="overline">
        Choose how to pay · ${amount}
        {returnLeg && tipAmount === 0 && (
          <span className="ml-2 text-[#D4AF37] normal-case tracking-normal" data-testid="rt-pay-note">
            (both trips)
          </span>
        )}
        {tipAmount > 0 && (
          <span className="ml-2 text-[#D4AF37] normal-case tracking-normal" data-testid="tip-summary">
            ({returnLeg ? "both trips" : "ride"} ${baseAmount.toFixed(2)} + tip ${tipAmount.toFixed(2)})
          </span>
        )}
      </div>

      {/* Customer tip — additional gratuity layered on top of the booking
          total. Quick-pick % buttons use the booking.fare as the base (already
          gratuity-bundled in the customer view, so this is genuine extra
          appreciation). Hidden in dispatcher mode since dispatch controls
          gratuity through the Fare Breakdown panel. */}
      {canOfferTip && (picked === "card" || picked === "on_file" || picked === null) && (
        <div className="border border-white/10 bg-[#0A0A0A] p-4 space-y-3" data-testid="tip-block">
          <div className="flex items-center justify-between">
            <div className="overline text-[#D4AF37]">Add a tip · optional</div>
            {tipAmount > 0 && (
              <button
                type="button"
                data-testid="tip-clear"
                onClick={() => setTip(0)}
                className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] hover:text-white"
              >Clear</button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {[10, 15, 20, 25].map(pct => {
              const v = +(baseAmount * pct / 100).toFixed(2);
              const active = Math.abs(tipAmount - v) < 0.005;
              return (
                <button
                  key={pct}
                  type="button"
                  data-testid={`tip-pct-${pct}`}
                  onClick={() => setTip(v)}
                  className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 border transition-colors ${active ? "border-[#D4AF37] bg-[#D4AF37] text-black" : "border-white/15 text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37]"}`}
                >{pct}% · ${v.toFixed(2)}</button>
              );
            })}
            <div className="flex items-center gap-1.5 border border-white/15 px-2.5 py-1 text-[11px] hover:border-[#D4AF37] focus-within:border-[#D4AF37] transition-colors">
              <span className="text-[#71717A]">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={tip || ""}
                onChange={e => setTip(e.target.value === "" ? 0 : parseFloat(e.target.value) || 0)}
                placeholder="Custom"
                data-testid="tip-custom"
                className="w-20 bg-transparent text-[#F5F2EB] text-sm focus:outline-none placeholder:text-[#71717A]"
              />
            </div>
          </div>
          <div className="text-[10px] text-[#71717A]">
            100% of tips go to the driver · You can also tip in cash on the ride.
          </div>
        </div>
      )}

      {/* Method tabs */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
        {savedCards.length > 0 && (
          <MethodBtn id="on_file" picked={picked} onPick={setPicked} icon={<ShieldCheck size={16}/>} label="On File"/>
        )}
        <MethodBtn id="card" picked={picked} onPick={setPicked} icon={<CardIcon size={16}/>} label="Card"/>
        {methods?.zelle?.enabled !== false && (
          <MethodBtn id="zelle" picked={picked} onPick={setPicked} icon={<Smartphone size={16}/>} label="Zelle"/>
        )}
        {methods?.venmo?.enabled !== false && (
          <MethodBtn id="venmo" picked={picked} onPick={setPicked} icon={<Smartphone size={16}/>} label="Venmo"/>
        )}
        {methods?.cash?.enabled !== false && (
          <MethodBtn id="cash" picked={picked} onPick={setPicked} icon={<Banknote size={16}/>} label="Cash"/>
        )}
        <MethodBtn id="later" picked={picked} onPick={setPicked} icon={<Clock size={16}/>} label="Pay later"/>
      </div>

      {/* Saved cards (Card on File) */}
      {picked === "on_file" && (
        <div className="border border-white/10 bg-[#0A0A0A] p-5 space-y-3">
          <div className="text-xs text-[#A1A1AA] flex items-center gap-2 mb-2">
            <ShieldCheck size={14} className="text-[#D4AF37]"/>
            Charge a card you&apos;ve previously saved · secured by Square
          </div>
          {savedCards.map(c => (
            <button
              key={c.saved_card_id}
              data-testid={`pay-saved-${c.saved_card_id}`}
              disabled={busy}
              onClick={() => chargeSaved(c.saved_card_id)}
              className="w-full border border-white/10 bg-[#050505] hover:border-[#D4AF37]/50 hover:bg-[#0e0e0e] transition-colors p-4 flex items-center justify-between disabled:opacity-50"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-8 bg-[#D4AF37] text-[#0a0a0a] flex items-center justify-center text-[10px] font-bold tracking-wider">
                  {BRAND_LABEL[c.brand] || "CARD"}
                </div>
                <div className="text-left">
                  <div className="text-sm tracking-[0.18em] text-[#F5F2EB]">•••• {c.last4}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
                    exp {String(c.exp_month).padStart(2, "0")}/{String(c.exp_year).slice(-2)}
                  </div>
                </div>
              </div>
              <span className="text-[#D4AF37] text-xs uppercase tracking-[0.2em]">
                {busy ? "Charging…" : `Charge $${amount}`}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Square Card */}
      {picked === "card" && methods.card?.enabled && (
        <div className="border border-white/10 bg-[#0A0A0A] p-5">
          <div className="text-xs text-[#A1A1AA] mb-4">Secure payment via Square · Apple Pay & Google Pay supported.</div>
          <PaymentForm
            applicationId={methods.card.app_id}
            locationId={methods.card.location_id}
            cardTokenizeResponseReceived={handleSquareToken}
            createPaymentRequest={() => ({
              countryCode: "US",
              currencyCode: "USD",
              total: { amount, label: `Delux Limo · ${booking.confirmation_code}` },
            })}
          >
            <div className="space-y-3">
              <ApplePay/>
              <GooglePay/>
              <CreditCard
                buttonProps={{
                  css: {
                    backgroundColor: "#D4AF37",
                    color: "#050505",
                    fontFamily: "Outfit, sans-serif",
                    fontWeight: 600,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    fontSize: "12px",
                    "&:hover": { backgroundColor: "#C29B2E" },
                  }
                }}
              >
                {busy ? "Processing…" : `Pay $${amount}`}
              </CreditCard>
            </div>
          </PaymentForm>
          {!dispatcherMode && (
            <div className="mt-4 text-[11px] text-[#A1A1AA] leading-snug" data-testid="save-card-auto-note">
              <span className="text-[#F5F2EB]">Your card is saved automatically</span> as a Payment Method for one-tap pay on future bookings · stored securely with Square (we only see brand + last 4) · remove anytime from your profile.
            </div>
          )}
        </div>
      )}
      {picked === "card" && !methods.card?.enabled && (
        <div className="border border-white/10 bg-[#0A0A0A] p-5 text-sm text-[#A1A1AA]">
          Card payments are not configured yet. Choose Zelle, Venmo, Cash, or Pay later.
        </div>
      )}

      {/* Zelle */}
      {picked === "zelle" && (
        <ManualBlock
          title="Zelle"
          handle={methods.zelle.handle}
          handleLabel="Send to"
          instructions={methods.zelle.instructions}
          memoHint={`Include code ${booking.confirmation_code} in the memo`}
          onConfirm={(ref) => handleManual("zelle", ref)}
          busy={busy}
        />
      )}

      {/* Venmo */}
      {picked === "venmo" && (
        <ManualBlock
          title="Venmo"
          handle={methods.venmo.handle}
          handleLabel="Send to"
          instructions={methods.venmo.instructions}
          memoHint={`Include code ${booking.confirmation_code} in the note`}
          onConfirm={(ref) => handleManual("venmo", ref)}
          busy={busy}
        />
      )}

      {/* Cash */}
      {picked === "cash" && (
        <div className="border border-white/10 bg-[#0A0A0A] p-5 space-y-4">
          <div className="text-sm text-[#F5F2EB]">{methods.cash.instructions}</div>
          <div className="text-xs text-[#71717A]">No surprises — your chauffeur will know the exact total: <span className="text-[#D4AF37]">${amount}</span></div>
          <button
            data-testid="confirm-cash"
            disabled={busy}
            onClick={() => handleManual("cash")}
            className="btn-gold w-full"
          >Confirm — pay cash on arrival</button>
        </div>
      )}

      {/* Pay later */}
      {picked === "later" && (
        <div className="border border-white/10 bg-[#0A0A0A] p-5 space-y-4">
          <div className="text-sm text-[#F5F2EB]">{methods.pay_later.instructions}</div>
          <button
            data-testid="confirm-pay-later"
            disabled={busy}
            onClick={() => handleManual("cash")}
            className="btn-ghost w-full"
          >Pay later — confirm</button>
        </div>
      )}
    </div>
  );
}

function MethodBtn({ id, picked, onPick, icon, label }) {
  const active = picked === id;
  return (
    <button
      data-testid={`pay-method-${id}`}
      onClick={() => onPick(id)}
      className={`flex flex-col items-center gap-2 py-3 px-3 border text-xs uppercase tracking-[0.16em] transition ${active ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-white/10 text-[#A1A1AA] hover:border-white/30'}`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function ManualBlock({ title, handle, handleLabel, instructions, memoHint, onConfirm, busy }) {
  const [reference, setReference] = useState("");
  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-5 space-y-4">
      <div>
        <div className="overline mb-2">{title} payment</div>
        <div className="text-xs text-[#A1A1AA]">{instructions}</div>
      </div>
      <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA]">{handleLabel}</div>
        <div className="font-serif text-2xl text-[#D4AF37] mt-1">{handle}</div>
        <div className="text-xs text-[#71717A] mt-2">{memoHint}</div>
      </div>
      <div>
        <label className="field-label">Confirmation # (optional)</label>
        <input
          data-testid="manual-payment-ref"
          className="field-input"
          value={reference}
          onChange={e => setReference(e.target.value)}
          placeholder="e.g. Zelle/Venmo confirmation"
        />
      </div>
      <button
        data-testid="confirm-manual-payment"
        disabled={busy}
        onClick={() => onConfirm(reference || null)}
        className="btn-gold w-full"
      >I&apos;ve sent the payment</button>
      <p className="text-[10px] text-[#71717A] uppercase tracking-[0.18em]">Dispatch will verify receipt before assigning your chauffeur.</p>
    </div>
  );
}
